import { Alert, Box, Skeleton } from '@citibox-panel/ui';
import type { BoxStatus } from '@citibox-panel/api-client';
import { useBoxStatus } from '../hooks/useBoxStatus';
import { boxStatusNotice } from '../lib/boxStatusNotice';
import { BoxDeliveriesShortcut } from './BoxDeliveriesShortcut';
import { BoxStatusErrorStrip } from './BoxStatusErrorStrip';

export interface BoxStatusBannerProps {
  boxId: string;
  // The three below feed the «Ver entregas» shortcut, not the notice itself. Required rather than
  // optional even though two of them are nullable: the banner only ever mounts where the detail has
  // all three to give, so an omission at the call site must be a type error and not a shortcut that
  // silently stops appearing.
  boxNumber: string;
  deliveryId: string;
  locationId: number | null;
  locationAddress: string | null;
  fetchBoxStatus: (boxId: string, deliveryId: string) => Promise<BoxStatus>;
}

// One line of copy: the skeleton and the error strip hold exactly the notice's footprint, or the slot
// changes height between states and reintroduces the shift the skeleton exists to prevent.
//
// ⚠️ Since CBX-11326 this no longer describes the RESOLVED notice when it carries «Ver entregas»:
// that notice is two lines, so the content below it drops ~32px once, when the read lands. The jump
// is DELIBERATE and was accepted over the alternative — reserving the taller footprint in every
// detail, which would leave dead space on every «Entrega» whose buzón has nothing to report. Do not
// "fix" it by growing this constant.
const SLOT_HEIGHT = 40;

// Owns the four states of one slot: a skeleton while the FIRST read is in flight (the refetch after a
// write hides behind the detail's own skeleton), the resolved notice, a NEUTRAL strip + retry on
// failure, and nothing at all for the no-banner outcomes. The strip is deliberately quieter than the
// notices it sits among: until app-service ships the read it is what every detail with a «buzón»
// shows, and red there would read as a problem with the delivery. Nothing the wire sent is ever
// rendered — the copy is resolved from the case alone.
export function BoxStatusBanner({
  boxId,
  boxNumber,
  deliveryId,
  locationId,
  locationAddress,
  fetchBoxStatus,
}: BoxStatusBannerProps) {
  const { status, isPending, isError, refetch } = useBoxStatus(boxId, deliveryId, fetchBoxStatus);

  if (isPending) {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Skeleton variant="rounded" width="100%" height={SLOT_HEIGHT} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <BoxStatusErrorStrip slotHeight={SLOT_HEIGHT} onRetry={refetch} />
      </Box>
    );
  }

  // Fail closed: an unknown case renders nothing rather than guessing a tone.
  const notice = status === undefined ? null : boxStatusNotice(status.case);
  if (notice === null) return null;

  // Where the «Ver entregas» shortcut is offered, in three conditions that must all hold.
  //
  // The first hangs off the TONE, not off a second list of cases: `warning` already means «another
  // delivery touched this buzón», which is exactly when there is something to go and look at, and
  // reading it this way inherits the notice's fail-closed — an unknown case resolves no notice, and
  // with no notice there is no button. The green case is the one asserting there is nothing to see.
  //
  // The other two are about DATA: with no id there is no ubicación to open, and with no address the
  // tab would be created label-less and `normalizeShellState` would drop it on the spot — a button
  // that opens a tab which destroys itself is worse than no button at all. The id guard doubles as
  // the delivery strategy: the field is mirrored `nullish`, so this ships before app-service
  // publishes it and lights up on its own, with no panel release.
  //
  // Resolved into the TARGET rather than a boolean so the two nullable values narrow for the
  // handler, instead of being asserted non-null a second time where it is written.
  const deliveriesTarget =
    notice.tone === 'warning' && locationId !== null && locationAddress !== null
      ? { id: locationId, address: locationAddress }
      : null;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Alert variant="notice" severity={notice.tone}>
        {notice.message}
        {deliveriesTarget !== null && (
          <Box sx={{ mt: 0.5 }}>
            <BoxDeliveriesShortcut
              boxNumber={boxNumber}
              locationId={deliveriesTarget.id}
              locationAddress={deliveriesTarget.address}
            />
          </Box>
        )}
      </Alert>
    </Box>
  );
}
